import React from 'react';
import { FormRenderProps } from '@progress/kendo-react-form';
import {
  buildCurrentFormValues,
  buildFormValuesWithClearedDependents,
  clearDependentFields,
  fillFormFieldsFromResponse,
  getDependentFields,
  isValueEmpty,
  shouldEndDueToFileDependency,
  shouldTriggerFormFiller
} from './FieldInteractionsHelper';
import { toFormFieldValue } from '../fields/formFieldValueHelpers';
import type { FieldInteractionCallbacks } from './fieldInteractionsTypes';
import {
  ContentTypeFieldResponse,
  CpsDocumentMetadata,
  LoadFieldValuesRequest,
  SEARCHABLE_FIELD_DEFAULT_PAGE,
  SEARCHABLE_FIELD_DEFAULT_PAGE_SIZE
} from '@cs-cms/types';
import type { useFieldState } from './useFieldState';

type FieldState = ReturnType<typeof useFieldState>;

interface UseFieldInteractionApiParams {
  ctFields: ContentTypeFieldResponse[];
  contentTypeFields: ContentTypeFieldResponse[];
  selectedCtName?: string;
  selectedIndexId?: number;
  fieldState: FieldState;
  loadFieldValues: (params: {
    fieldId: number;
    request: LoadFieldValuesRequest;
  }) => Promise<{ values?: string[]; valuesForOtherFields?: Record<string, unknown> } | null | undefined>;
  fieldInteractionCallbacks?: FieldInteractionCallbacks;
}

export interface UseFieldInteractionApiReturn {
  callLoadFieldValuesApi: (
    fieldName: string,
    currentFormValues: CpsDocumentMetadata,
    searchText?: string
  ) => Promise<{ values?: string[]; valuesForOtherFields?: Record<string, unknown> } | null | undefined>;
  applyLookupResponseToForm: (
    response: { valuesForOtherFields?: Record<string, unknown> } | null | undefined,
    formRenderProps: FormRenderProps
  ) => Promise<void>;
  handleDependencies: (fieldName: string, value: unknown, formRenderProps: FormRenderProps) => Promise<void>;
  runLookupForField: (
    fieldName: string,
    value: unknown,
    formRenderProps: FormRenderProps
  ) => Promise<{ valuesForOtherFields?: Record<string, unknown> } | null>;
}

export const useFieldInteractionApi = ({
  ctFields,
  contentTypeFields,
  selectedCtName,
  selectedIndexId,
  fieldState,
  loadFieldValues,
  fieldInteractionCallbacks
}: UseFieldInteractionApiParams): UseFieldInteractionApiReturn => {
  const onAfterFilledFieldsFromLookup = fieldInteractionCallbacks?.onAfterFilledFieldsFromLookup;
  const onAfterClearedDependentFields = fieldInteractionCallbacks?.onAfterClearedDependentFields;

  const lookupChainRef = React.useRef<{
    processedFieldNames: Set<string>;
    chainDepth: number;
  }>({
    processedFieldNames: new Set<string>(),
    chainDepth: 0
  });

  const startLookupChainIfNeeded = (): void => {
    if (lookupChainRef.current.chainDepth === 0) {
      lookupChainRef.current = {
        processedFieldNames: new Set<string>(),
        chainDepth: 0
      };
    }
  };

  const tryMarkProcessed = (fieldName: string): boolean => {
    const processed = lookupChainRef.current.processedFieldNames;
    if (processed.has(fieldName)) {
      return false;
    }
    processed.add(fieldName);
    return true;
  };

  const runLookupForFieldInternal = async (
    fieldName: string,
    value: unknown,
    formRenderProps: FormRenderProps,
    options: { resetChain: boolean }
  ): Promise<{ valuesForOtherFields?: Record<string, unknown> } | null> => {
    const field = ctFields.find(f => f.name === fieldName);
    if (!field?.usesExtDictionary || isValueEmpty(value)) {
      return null;
    }

    if (options.resetChain) {
      lookupChainRef.current = {
        processedFieldNames: new Set<string>([fieldName]),
        chainDepth: 0
      };
    } else {
      startLookupChainIfNeeded();
      tryMarkProcessed(fieldName);
    }

    const currentFormValues = buildCurrentFormValues(fieldName, value, formRenderProps, ctFields);
    const response = await callLoadFieldValuesApi(fieldName, currentFormValues);

    await applyLookupResponseToForm(response, formRenderProps);
    return response as { valuesForOtherFields?: Record<string, unknown> } | null;
  };

  const applyLookupResponseToForm = async (
    response: { valuesForOtherFields?: Record<string, unknown> } | null | undefined,
    formRenderProps: FormRenderProps
  ): Promise<void> => {
    fillFormFieldsFromResponse(response, formRenderProps);

    if (response?.valuesForOtherFields && onAfterFilledFieldsFromLookup) {
      onAfterFilledFieldsFromLookup({
        targetFieldNames: Object.keys(response.valuesForOtherFields),
        formRenderProps,
        contentTypeFields
      });
    }

    if (!response?.valuesForOtherFields) {
      return;
    }

    startLookupChainIfNeeded();
    lookupChainRef.current.chainDepth += 1;

    try {
      for (const [filledFieldName, filledValue] of Object.entries(response.valuesForOtherFields)) {
        if (!tryMarkProcessed(filledFieldName)) {
          continue;
        }

        await handleDependencies(filledFieldName, filledValue, formRenderProps);

        const filledField = ctFields.find(f => f.name === filledFieldName);
        if (!filledField || shouldEndDueToFileDependency(filledField)) {
          continue;
        }

        if (shouldTriggerFormFiller(filledField, filledValue)) {
          await runLookupForFieldInternal(filledFieldName, filledValue, formRenderProps, { resetChain: false });
        }
      }
    } finally {
      lookupChainRef.current.chainDepth -= 1;
    }
  };

  const clearDependentFieldsWithOptionalHook = (dependentFields: string[], formRenderProps: FormRenderProps): void => {
    clearDependentFields(dependentFields, formRenderProps);

    if (!onAfterClearedDependentFields || dependentFields.length === 0) {
      return;
    }

    onAfterClearedDependentFields({
      clearedFieldNames: dependentFields,
      formRenderProps,
      contentTypeFields
    });
  };

  const callLoadFieldValuesApi = async (
    fieldName: string,
    currentFormValues: CpsDocumentMetadata,
    searchText?: string
  ) => {
    const field = ctFields.find(f => f.name === fieldName);
    if (!field || !selectedCtName || selectedIndexId == null) {
      return null;
    }

    fieldState.addLoadingField(fieldName);

    try {
      const request: LoadFieldValuesRequest = {
        contentTypeName: selectedCtName,
        metadata: currentFormValues,
        ...(searchText && {
          searchText,
          page: SEARCHABLE_FIELD_DEFAULT_PAGE,
          pageSize: SEARCHABLE_FIELD_DEFAULT_PAGE_SIZE
        })
      };

      const fieldId = field.id ?? -1;
      return await loadFieldValues({ fieldId, request });
    } finally {
      fieldState.removeLoadingField(fieldName);
    }
  };

  const continueLookupChainForFieldValue = async (
    fieldName: string,
    value: unknown,
    formRenderProps: FormRenderProps
  ): Promise<void> => {
    const field = ctFields.find(f => f.name === fieldName);
    if (!field || shouldEndDueToFileDependency(field)) {
      return;
    }

    await handleDependencies(fieldName, value, formRenderProps);

    if (shouldTriggerFormFiller(field, value)) {
      await runLookupForFieldInternal(fieldName, value, formRenderProps, { resetChain: false });
    }
  };

  const loadDependentChoiceOptions = async (
    dependentFieldName: string,
    currentFormValues: CpsDocumentMetadata,
    formRenderProps: FormRenderProps
  ) => {
    const dependentField = ctFields.find(f => f.name === dependentFieldName);
    if (!dependentField?.usesExtDictionary) {
      return;
    }

    const response = await callLoadFieldValuesApi(dependentFieldName, currentFormValues);

    if (response?.values && response.values.length > 0) {
      fieldState.setFieldChoices(dependentFieldName, response.values);
      fieldState.addEnabledField(dependentFieldName);

      if (response.values.length === 1) {
        const autoFilledValue = response.values[0];
        const formValue = toFormFieldValue(dependentField, autoFilledValue);
        formRenderProps.onChange(dependentFieldName, { value: formValue });
        await continueLookupChainForFieldValue(dependentFieldName, autoFilledValue, formRenderProps);
      }
    } else {
      fieldState.clearFieldChoices(dependentFieldName);
      fieldState.removeEnabledField(dependentFieldName);
    }
  };

  const handleDependencies = async (fieldName: string, value: unknown, formRenderProps: FormRenderProps) => {
    const dependentFields = getDependentFields(fieldName, ctFields);

    if (dependentFields.length === 0) {
      return;
    }

    clearDependentFieldsWithOptionalHook(dependentFields, formRenderProps);

    const currentFormValues = buildFormValuesWithClearedDependents(
      fieldName,
      value,
      formRenderProps,
      ctFields,
      dependentFields
    );

    for (const dependentField of dependentFields) {
      await loadDependentChoiceOptions(dependentField, currentFormValues, formRenderProps);
    }
  };

  const runLookupForField = async (
    fieldName: string,
    value: unknown,
    formRenderProps: FormRenderProps
  ): Promise<{ valuesForOtherFields?: Record<string, unknown> } | null> => {
    return runLookupForFieldInternal(fieldName, value, formRenderProps, { resetChain: true });
  };

  return {
    callLoadFieldValuesApi,
    applyLookupResponseToForm,
    handleDependencies,
    runLookupForField
  };
};
